import { cn } from "@paribus/shared-ui";
import { Upload } from "lucide-react";
import React from "react";
import { useDropzone } from "react-dropzone";
import type { Accept, FileRejection } from "react-dropzone";

export interface DropzoneProps {
  onFiles?: (files: File[]) => void;
  onReject?: (rejections: FileRejection[]) => void;
  accept?: Accept;
  multiple?: boolean;
  disabled?: boolean;
  hint?: string;
  variant?: "full" | "compact";
  className?: string;
}

export function Dropzone({
  onFiles,
  onReject,
  accept,
  multiple,
  disabled,
  hint,
  variant = "full",
  className,
}: DropzoneProps) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (accepted, rejected) => {
      if (accepted.length > 0) onFiles?.(accepted);
      if (rejected.length > 0) onReject?.(rejected);
    },
    accept,
    multiple,
    disabled,
  });

  if (variant === "compact") {
    return (
      <div
        {...getRootProps()}
        aria-disabled={disabled}
        className={cn(
          "flex cursor-pointer items-center gap-3 rounded-2xl border-2 border-dashed px-4 py-3 transition-colors",
          isDragActive
            ? "border-primary bg-primary-subtle"
            : "border-primary/40 bg-primary-subtle/20",
          disabled ? "pointer-events-none cursor-not-allowed opacity-50" : "",
          className
        )}
      >
        <input {...getInputProps()} />
        <Upload size={20} className="flex-shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">
            {isDragActive ? "Drop files here" : "Drag files here or click to upload"}
          </p>
          {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
        </div>
      </div>
    );
  }

  return (
    <div
      {...getRootProps()}
      aria-disabled={disabled}
      className={cn(
        "flex min-h-[196px] cursor-pointer flex-col items-center justify-center gap-3 rounded-[1.5rem] border-2 border-dashed p-8 text-center transition-colors",
        "bg-[radial-gradient(ellipse_at_50%_30%,hsl(var(--primary)/0.18)_0%,transparent_70%)]",
        isDragActive
          ? "border-primary bg-primary-subtle"
          : "border-primary/40 bg-primary-subtle/20",
        disabled ? "pointer-events-none cursor-not-allowed opacity-50" : "",
        className
      )}
    >
      <input {...getInputProps()} />
      <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary-subtle ring-1 ring-inset ring-primary-border">
        <Upload size={26} className="text-primary-emphasis" />
      </div>
      <p className="text-sm font-semibold text-foreground">
        {isDragActive ? "Drop files here" : "Drag files here or click to upload"}
      </p>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
